/**
 * MarketingHomePage — the public front door at `/` for logged-out visitors.
 *
 * V3 (2026-06-12): "The Night Shift" — the connective redesign that replaced
 * the V2 golden road (concept exploration + the saved Signal Path draft live
 * in docs/prototypes/). One page, one story, and TIME is the connective
 * tissue: after the hero + money shot, you log off and Avium clocks in. A
 * sticky clock runs 11 PM → 7 AM as you scroll, the sky behind the page
 * turns from deep night (stars) to dawn, and each beat is a timestamped
 * event in Avium's overnight shift:
 *
 *   11:02 PM sync (one connection) → 1:36 AM the Signals sweep (Risk
 *   Ledger) → 3:11 AM the odds (What-If 61%→84%) → 5:27 AM the receipts
 *   (drill to the ticket) → 6:40 AM the Knowledge Graph resolves → 7:00 AM
 *   sunrise: "Your phone buzzes. The brief is waiting."
 *
 * Everything still tells ONE numeric story (Sprint 12 · 61% → 84% via the
 * AV-216 move) so no two surfaces disagree. The SEO cluster links every
 * persona + Signal in the "go deeper" band above the footer (pinned by
 * MarketingHomePage.test). Beat ⑤ mounts the real KnowledgeGraphPreview
 * (the ONE canonical KG visual — the messy→structured morph): the page
 * opens and closes on the same living graphic.
 *
 * Logged-in users never see this (App's RootRoute sends them into the app).
 */

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../lib/useDocumentMeta';
import PublicHeader from '../components/shared/PublicHeader';
import PublicFooter from '../components/shared/PublicFooter';
import KnowledgeGraphPreview from '../components/marketing/KnowledgeGraphPreview';
import LeadershipBriefingPreview from '../components/marketing/LeadershipBriefingPreview';
import { PERSONA_LEARN_CONTENT } from '../lib/personaLearnContent';
import { SIGNAL_LEARN_CONTENT } from '../lib/signalLearnContent';

const CSS = `
  .mhv2 {
    /* ── the landing color system — anchored on the marketing/email asset
          (brand-centralization rule): amber = labels/accents · bold dark-green
          ink = values · dark red = ONE deliberate risk emphasis per card.
          The page background is the SKY (fixed layers below): night → dawn. ── */
    --mh-card:#FFFFFF; --mh-card2:#FFFFFF;
    --mh-ink:#0B3B38; --mh-body:#46706A; --mh-dim:#7A938E;
    --mh-amber:#B45309; --mh-amber-strong:#C2410C; --mh-amber-bright:#D97706;
    --mh-risk:#B91C1C;
    --mh-badge:#E0613A;
    color:#fff; background:transparent;
    overflow-x:clip; } /* no card's intrinsic width may widen the page */
  /* the page is a flex COLUMN; items default to min-width:auto, so one wide
     card (What-If gauges, briefing panel) silently widens its whole section
     and shoves every surface past the right edge on phones. Never allow it. */
  .mhv2 > * { min-width:0; }
  .mhv2 .shot2 > * { min-width:0; }
  .mhv2 .w { max-width:1120px; margin:0 auto; padding:0 32px; }
  .mhv2 .btn-big { display:inline-block; background:#F97316; color:#fff; font-weight:700; font-size:16px;
                   padding:14px 28px; border-radius:9px; text-decoration:none;
                   box-shadow:0 10px 28px rgba(249,115,22,.4); transition:background .2s; }
  .mhv2 .btn-big:hover { background:#EA580C; }

  /* ── the sky: night base, dawn crossfades in, stars fade out ── */
  .mhv2 .skyN { position:fixed; inset:0; z-index:-3;
                background:radial-gradient(circle at 80% 10%, rgba(20,184,166,.16) 0%, transparent 45%),
                           linear-gradient(180deg,#020D0B 0%,#04211D 55%,#06302B 100%); }
  .mhv2 .skyD { position:fixed; inset:0; z-index:-2; opacity:0;
                background:linear-gradient(180deg,#06302B 0%,#0B4A42 45%,#7C3A12 100%); }
  .mhv2 .sunglow { position:fixed; left:50%; bottom:-340px; width:min(900px,150vw); height:480px; z-index:-1;
                   transform:translateX(-50%); opacity:0; pointer-events:none;
                   background:radial-gradient(ellipse at center, rgba(249,115,22,.55) 0%,
                              rgba(251,191,36,.25) 40%, transparent 70%); }
  .mhv2 .stars { position:fixed; inset:0; z-index:-1; pointer-events:none; }
  .mhv2 .stars i { position:absolute; border-radius:50%; background:#E2FBF5; }
  @keyframes mhv2tw { 50% { opacity:.15; } }

  .mhv2 .hero { text-align:center; padding:60px 0 0; }
  .mhv2 .hero h1 { font-size:50px; font-weight:900; letter-spacing:-.025em; line-height:1.08; margin:0 auto; max-width:840px; text-wrap:balance; }
  .mhv2 .hero h1 .q { color:#5EEAD4; }
  .mhv2 .sub { font-size:18px; color:rgba(240,253,250,.55); margin:18px auto 0; max-width:620px; line-height:1.55; }
  .mhv2 .punch { font-size:27px; font-weight:800; letter-spacing:-.015em; color:#fff; margin:14px auto 0; max-width:760px; }
  .mhv2 .punch b { color:#FCD34D; font-weight:900; }
  .mhv2 .ctas { margin-top:28px; display:flex; gap:22px; justify-content:center; align-items:center; }

  .mhv2 .shot2 { display:flex; gap:18px; margin:46px auto 0; text-align:left; position:relative; z-index:2; }
  .mhv2 .kgp-panel { flex:1.3; min-width:0; border:1px solid rgba(11,59,56,.08); border-radius:16px;
                     background:
                       radial-gradient(700px 240px at 75% 0%, rgba(15,118,110,.05), transparent 60%),
                       #FFFFFF;
                     box-shadow:0 26px 60px rgba(2,16,14,.55), 0 2px 8px rgba(11,59,56,.12); padding:20px 22px 14px; }
  .mhv2 .kgp-hd { font-size:24px; font-weight:800; letter-spacing:-.025em; color:#0F766E; text-transform:uppercase; line-height:1; margin-bottom:10px; display:flex; align-items:baseline; gap:8px; }
  .mhv2 .kgp-hd i { color:#F59E0B; font-style:normal; }
  .mhv2 .kgp-hd span { color:#7A938E; font-weight:600; font-size:11px; letter-spacing:0; text-transform:none; margin-left:auto; }
  .mhv2 .bfp-panel { flex:1; min-width:0; display:flex; }
  .mhv2 .bfp-panel > * { width:100%; }
  .mhv2 .shotcap { text-align:center; font-size:19px; font-weight:600; color:rgba(240,253,250,.82);
                   margin:22px auto 0; max-width:860px; line-height:1.5; position:relative; z-index:2; }
  .mhv2 .shotcap b { color:#5EEAD4; font-weight:800; }

  /* the hand-off into the night — it OPENS the story, so it reads like a
     chapter heading, not a caption */
  .mhv2 .handoff { text-align:center; font-size:29px; font-weight:800; letter-spacing:-.015em;
                   color:rgba(240,253,250,.85); margin:64px auto 10px; max-width:760px; line-height:1.5; }
  .mhv2 .handoff b { color:#5EEAD4; }

  /* ── THE NIGHT: sticky clock + time rail + timestamped beats ── */
  .mhv2 .night { position:relative; max-width:1240px; margin:0 auto; }
  .mhv2 .hud { position:sticky; top:74px; z-index:10; display:flex; justify-content:center; }
  .mhv2 .hudpill { display:flex; align-items:center; gap:12px; background:rgba(3,17,15,.82);
                   border:1px solid rgba(94,234,212,.25); border-radius:999px; padding:9px 20px;
                   backdrop-filter:blur(8px); box-shadow:0 8px 30px rgba(0,0,0,.45); }
  .mhv2 .hudpill b { font-size:21px; font-weight:900; letter-spacing:.02em; min-width:104px;
                     font-variant-numeric:tabular-nums; text-align:center; }
  .mhv2 .hudpill .cap { font-size:12px; font-weight:600; color:rgba(240,253,250,.55); }
  .mhv2 .orb { width:15px; height:15px; border-radius:50%; background:#CBD5E1; flex:none; }

  .mhv2 .rail { position:absolute; left:96px; top:0; bottom:0; width:2px; background:rgba(94,234,212,.12); }
  .mhv2 .railfill { position:absolute; left:96px; top:0; width:2px; height:0;
                    background:linear-gradient(180deg,#14B8A6,#FBBF24);
                    box-shadow:0 0 14px rgba(251,191,36,.4); }
  .mhv2 .nowdot { position:absolute; left:96px; top:0; width:10px; height:10px; margin-left:-4px;
                  margin-top:-5px; border-radius:50%; background:#FFFBEB;
                  box-shadow:0 0 18px rgba(251,191,36,.85); z-index:4; }

  .mhv2 .nsec { position:relative; }
  .mhv2 .stamp { position:absolute; left:96px; top:50%; transform:translate(-50%,-50%);
                 background:rgba(3,17,15,.92); border:1px solid rgba(94,234,212,.3);
                 color:rgba(240,253,250,.5); font-weight:800; font-size:12.5px;
                 font-variant-numeric:tabular-nums; padding:7px 13px; border-radius:999px;
                 white-space:nowrap; z-index:3;
                 transition:background .45s, color .45s, border-color .45s, box-shadow .45s; }
  .mhv2 .stamp.lit { color:#3B1D04; border-color:rgba(255,251,235,.55);
                     background:linear-gradient(135deg,#FBBF24,#F97316);
                     box-shadow:0 0 26px rgba(251,191,36,.6); }

  .mhv2 .nstrip { position:relative; z-index:2; padding:38px 64px 38px 220px; font-size:18px;
                  font-weight:600; color:rgba(240,253,250,.82);
                  border-top:1px solid rgba(94,234,212,.14); border-bottom:1px solid rgba(94,234,212,.14);
                  background:rgba(94,234,212,.04); }
  .mhv2 .nstrip b { color:#5EEAD4; }
  .mhv2 .nbeat { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:center;
                 padding:96px 64px 96px 220px; }
  .mhv2 .nbeat .txt { position:relative; z-index:2; }
  .mhv2 .lab { font-size:18px; font-weight:800; letter-spacing:.1em; color:#14B8A6; text-transform:uppercase; }
  .mhv2 .nbeat h2 { font-size:38px; font-weight:900; letter-spacing:-.02em; margin-top:10px; line-height:1.16; color:#fff;
                    text-wrap:balance; } /* balanced line breaks — no orphaned last word */
  .mhv2 .beatbody { font-size:16px; color:rgba(240,253,250,.82); margin-top:14px; line-height:1.6; max-width:460px; }
  .mhv2 .nbeat .ledger, .mhv2 .nbeat .wif, .mhv2 .nbeat .drill { max-width:375px; width:100%; justify-self:center; }

  .mhv2 .gcard { border:1px solid rgba(11,59,56,.16); border-radius:14px; background:linear-gradient(160deg,var(--mh-card),var(--mh-card2));
                 padding:20px; box-shadow:0 18px 50px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.7); }

  .mhv2 .ledger { display:flex; flex-direction:column; gap:0; }
  .mhv2 .lhead { font-size:11px; font-weight:800; letter-spacing:.1em; color:var(--mh-amber-strong); display:flex; justify-content:space-between; padding-bottom:10px; border-bottom:1px solid rgba(11,59,56,.1); }
  .mhv2 .lrow { padding:14px 2px; }
  .mhv2 .lrow + .lrow { border-top:1px solid rgba(11,59,56,.1); }
  .mhv2 .lrow .top { display:flex; align-items:center; gap:9px; }
  .mhv2 .lrow .rank { color:var(--mh-amber); font-size:15px; font-weight:900; }
  .mhv2 .lrow .ti { font-weight:800; color:var(--mh-ink); font-size:14px; flex:1; }
  .mhv2 .pillOff { font-size:10px; font-weight:800; border-radius:999px; padding:3px 9px;
                   background:var(--mh-badge); color:#fff; border:1px solid var(--mh-badge); }
  .mhv2 .lrow .mid { display:flex; align-items:center; gap:12px; margin-top:8px; }
  .mhv2 .lmetric { font-size:25px; font-weight:900; color:var(--mh-ink); line-height:1; white-space:nowrap; }
  .mhv2 .lmetric.risk { color:var(--mh-risk); }
  .mhv2 .lrow .dt { font-size:12px; color:var(--mh-body); line-height:1.45; }
  .mhv2 .lrow .src { margin-top:8px; font-size:10px; color:var(--mh-dim); }
  .mhv2 .lrow .src b { color:var(--mh-amber); font-weight:800; }

  .mhv2 .rise { transform:perspective(1200px) translateY(28px) rotateX(7deg) scale(.96); opacity:.15;
                transition:transform .7s cubic-bezier(.2,.7,.2,1), opacity .7s, box-shadow .35s; }
  .mhv2 .rise.in { transform:perspective(1200px) translateY(0) rotateX(0) scale(1); opacity:1; }
  .mhv2 .rise:hover { transform:perspective(1200px) translateY(-4px) scale(1.02); }

  .mhv2 .wif { border:1px solid rgba(11,59,56,.16); border-radius:14px; background:linear-gradient(160deg,var(--mh-card),var(--mh-card2));
               padding:18px 20px 14px; box-shadow:0 18px 50px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.7); position:relative; }
  .mhv2 .wif .delta { position:absolute; top:14px; right:16px; background:rgba(15,118,110,.1); border:1px solid rgba(15,118,110,.4);
                      color:#0F766E; font-weight:900; font-size:15px; border-radius:10px; padding:5px 11px; }
  .mhv2 .wif .delta small { display:block; font-size:8.5px; font-weight:800; letter-spacing:.08em; color:#7A938E; }
  .mhv2 .wifrow { display:flex; align-items:center; justify-content:space-between; gap:6px; margin-top:4px; }
  .mhv2 .gbox { text-align:center; flex:none; }
  .mhv2 .gbox svg { width:104px; height:62px; }
  .mhv2 .gbox .gl { font-size:9.5px; font-weight:800; letter-spacing:.1em; color:var(--mh-dim); margin-top:-2px; }
  .mhv2 .chipL { display:inline-block; margin-top:5px; font-size:10px; font-weight:800; border-radius:999px; padding:2.5px 9px; }
  .mhv2 .c-red { background:var(--mh-badge); color:#fff; border:1px solid var(--mh-badge); }
  .mhv2 .c-teal { background:rgba(15,118,110,.1); color:#0F766E; border:1px solid rgba(15,118,110,.4); }
  .mhv2 .gbox.today { opacity:.62; }
  .mhv2 .lever { flex:1; text-align:center; }
  .mhv2 .lever .arrowline { color:#D97706; font-size:20px; font-weight:900; line-height:1; }
  .mhv2 .lever .lc { display:inline-block; margin-top:6px; background:#F0FAF8; border-left:3px solid #F97316;
                     color:var(--mh-ink); font-size:10px; font-weight:800; border-radius:0 8px 8px 0; padding:5px 9px; line-height:1.35; text-align:left; }
  .mhv2 .wiffoot { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:12px; padding-top:10px;
                   border-top:1px solid rgba(11,59,56,.12); font-size:10.5px; color:#7A938E; }
  .mhv2 .wiffoot .mk { background:#0F766E; color:#fff; font-weight:800; font-size:11px; border-radius:8px;
                       padding:6px 12px; text-decoration:none; white-space:nowrap; }

  .mhv2 .drill { display:flex; flex-direction:column; font-size:13px; }
  .mhv2 .dr { border-radius:10px; padding:11px 14px; background:#F6F9F8;
              display:flex; align-items:center; gap:10px; }
  .mhv2 .dr .ic { font-size:16px; flex:none; }
  .mhv2 .dr .t { font-weight:800; color:var(--mh-ink); }
  .mhv2 .dr .m { color:var(--mh-dim); font-size:12px; }
  .mhv2 .dr.sig { background:#FFF4E8; }
  .mhv2 .dr.sig .t { color:var(--mh-amber); }
  .mhv2 .dr.ghost { border:1.5px dashed rgba(220,38,38,.4); background:#FDF3F2; }
  .mhv2 .dr.ghost .t { color:var(--mh-risk); }
  .mhv2 .dr.impact { background:#F0FAF8; border-left:3px solid #F97316; border-radius:0 10px 10px 0; }
  .mhv2 .badge { font-size:10px; font-weight:900; letter-spacing:.05em; text-transform:uppercase; border-radius:999px;
                 padding:3px 8px; flex:none; margin-left:auto; }
  .mhv2 .b-red { background:rgba(239,68,68,.1); color:var(--mh-risk); border:1px solid rgba(239,68,68,.35); }
  .mhv2 .b-amb { background:rgba(245,158,11,.16); color:var(--mh-amber); border:1px solid rgba(217,119,6,.45); }
  .mhv2 .hop { display:flex; align-items:center; gap:8px; padding:5px 0 5px 22px; color:var(--mh-amber-bright); font-weight:900; font-size:12px; }
  .mhv2 .hop span { color:var(--mh-dim); font-weight:700; font-size:10.5px; letter-spacing:.08em; text-transform:uppercase; }

  /* beat ⑤ — the real KnowledgeGraphPreview in a marquee card. The morph
     needs the same breathing room the hero money shot gets (~700px), so
     this beat goes single-column: text above, graph wide below. */
  .mhv2 .nsec.kgwide .nbeat { grid-template-columns:1fr; gap:30px; }
  .mhv2 .nsec.kgwide .nbeat .txt { max-width:600px; }
  .mhv2 .kgcard { max-width:800px; width:100%; justify-self:center; }
  .mhv2 .kgcard .kgbody { margin-top:12px; }
  .mhv2 .kgfoot { font-size:12px; color:var(--mh-body); border-top:1px solid rgba(11,59,56,.12);
                  margin-top:12px; padding-top:10px; line-height:1.5; }
  .mhv2 .kgfoot b { color:var(--mh-ink); display:block; font-size:13px; }

  .mhv2 .quoteband { position:relative; padding:64px 32px; }
  .mhv2 .quoteband .panel { border:1px solid rgba(94,234,212,.4); border-radius:18px; max-width:820px; margin:0 auto;
                            background:linear-gradient(160deg,#13564D,#0F4A43); padding:36px 44px;
                            box-shadow:0 18px 50px rgba(0,0,0,.5), inset 0 1px 0 rgba(94,234,212,.18); text-align:center;
                            position:relative; z-index:2; }
  .mhv2 .quoteband p { font-size:24px; font-weight:800; letter-spacing:-.015em; line-height:1.35; color:#fff; }
  .mhv2 .quoteband p span { color:#FCD34D; }
  .mhv2 .who { margin-top:14px; font-size:14px; color:rgba(240,253,250,.55); font-weight:600; }

  /* sunrise CTA */
  .mhv2 .ctaband { background:linear-gradient(125deg,#134E4A 0%,#0F766E 40%,#C2410C 80%,#F97316 100%); text-align:center;
                   padding:70px 24px; border-top:1px solid rgba(251,191,36,.25); position:relative; z-index:2; }
  .mhv2 .ctaband .when { font-size:15px; font-weight:800; letter-spacing:.14em; color:#FDE68A; text-transform:uppercase; }
  .mhv2 .ctaband h2 { font-size:36px; font-weight:900; letter-spacing:-.02em; margin-top:12px; text-wrap:balance; }
  .mhv2 .ctaband p { margin:14px auto 0; color:rgba(240,253,250,.92); font-size:16px; max-width:640px; }

  .mhv2 .deeper { border-top:1px solid rgba(94,234,212,.16); background:#051B18; padding:44px 0 50px; position:relative; z-index:2; }
  .mhv2 .deeper h3 { font-size:12px; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:#14B8A6; margin-bottom:14px; }
  .mhv2 .deeper .cols { display:grid; grid-template-columns:1.4fr 1fr; gap:44px; }
  .mhv2 .deeper a { color:rgba(240,253,250,.75); text-decoration:none; font-size:13.5px; font-weight:600; }
  .mhv2 .deeper a:hover { color:#5EEAD4; }
  .mhv2 .deeper .links { display:flex; flex-wrap:wrap; gap:8px 18px; }
  .mhv2 .deeper .hub { margin-top:14px; }
  .mhv2 .deeper .hub a { color:#5EEAD4; font-weight:800; }

  @media (prefers-reduced-motion: reduce) {
    .mhv2 .rise { transform:none; opacity:1; transition:none; }
    .mhv2 .stamp { transition:none; }
    .mhv2 .stars i { animation:none !important; }
  }
  @media (max-width: 900px) {
    .mhv2 .hero h1 { font-size:34px; }
    .mhv2 .punch { font-size:21px; }
    .mhv2 .shot2 { flex-direction:column; }
    .mhv2 .handoff { font-size:21px; padding:0 20px; }
    .mhv2 .hud { top:70px; }
    .mhv2 .hudpill b { font-size:17px; min-width:84px; }
    .mhv2 .rail, .mhv2 .railfill { left:44px; }
    .mhv2 .nowdot { left:44px; }
    .mhv2 .stamp { left:44px; font-size:10.5px; padding:5px 9px; }
    .mhv2 .nbeat { grid-template-columns:1fr; gap:28px; padding:64px 20px 64px 92px; }
    .mhv2 .nstrip { padding:28px 20px 28px 92px; }
    .mhv2 .quoteband { padding:48px 20px; }
    .mhv2 .deeper .cols { grid-template-columns:1fr; }
  }
  /* ── true phone widths: the fixed-width card internals (What-If gauges,
        ledger sparkline) exceed a 390px viewport's intrinsic space and were
        forcing the WHOLE page layout wide — every surface got cut at the
        right edge. Shrink/wrap them so nothing dictates page width. ── */
  @media (max-width: 480px) {
    .mhv2 .shot2 { padding:0 12px; }
    .mhv2 .nbeat { padding:56px 16px 56px 74px; gap:24px; }
    .mhv2 .nstrip { padding:24px 16px 24px 74px; }
    .mhv2 .nbeat > * { min-width:0; }
    .mhv2 .gbox svg { width:74px; height:45px; }
    .mhv2 .wifrow { flex-wrap:wrap; }
    .mhv2 .wif .delta { font-size:12px; padding:4px 8px; }
    .mhv2 .lever .lc { font-size:9px; }
    .mhv2 .lrow .mid svg { display:none; }  /* the sparkline can't earn its 84px here */
    .mhv2 .lmetric { font-size:20px; }
    .mhv2 .quoteband { padding:40px 16px; }
    .mhv2 .quoteband .panel { padding:24px 20px; }
    .mhv2 .quoteband p { font-size:18px; }
    .mhv2 .hudpill { padding:8px 14px; gap:9px; }
    .mhv2 .hudpill b { font-size:15px; min-width:74px; }
    .mhv2 .hudpill .cap { font-size:10.5px; }
  }
`;

/** Semicircle odds gauge — matches the in-app What-If gauges (r=52 arc). */
function OddsGauge({ pct, color }: { pct: number; color: string }) {
  const ARC = 163.4; // semicircle path length for r=52
  return (
    <svg viewBox="0 0 140 84" aria-label={`${pct}% on-time`}>
      <path d="M18 76 A52 52 0 0 1 122 76" fill="none" stroke="rgba(11,59,56,.14)" strokeWidth="11" strokeLinecap="round" />
      <path d="M18 76 A52 52 0 0 1 122 76" fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * ARC} ${ARC}`} />
      <text x="70" y="66" textAnchor="middle" fontSize="23" fontWeight="800" fill="#0B3B38">{pct}%</text>
    </svg>
  );
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const win = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));

/** the clock: 0 min = 11:00 PM, 480 min = 7:00 AM */
function fmtClock(m: number): string {
  const tot = (((1380 + Math.round(m)) % 1440) + 1440) % 1440;
  const h24 = Math.floor(tot / 60), mm = tot % 60;
  return `${((h24 + 11) % 12) + 1}:${String(mm).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

export default function MarketingHomePage() {
  useDocumentMeta({
    title: null, // → just "Avium" (the site name)
    description:
      'Hit delivery dates with confidence. Avium reads the Jira, Azure DevOps, Asana, or Excel you already run and delivers the answer every morning at 7: the risk, the odds on the date, and the one move that protects it.',
    path: '/',
  });

  const nightRef = useRef<HTMLDivElement | null>(null);
  const skyDRef = useRef<HTMLDivElement | null>(null);
  const sunglowRef = useRef<HTMLDivElement | null>(null);
  const starsRef = useRef<HTMLDivElement | null>(null);
  const railFillRef = useRef<HTMLDivElement | null>(null);
  const nowDotRef = useRef<HTMLDivElement | null>(null);
  const clockRef = useRef<HTMLElement | null>(null);
  const capRef = useRef<HTMLSpanElement | null>(null);
  const orbRef = useRef<HTMLSpanElement | null>(null);

  // The night engine: a single guarded rAF loop drives the clock, the sky,
  // the rail, the stamps, and the card rises — all from the scroll position.
  // (Same refs-not-rerenders pattern as KnowledgeGraphPreview.)
  useEffect(() => {
    const night = nightRef.current, skyD = skyDRef.current, sunglow = sunglowRef.current,
      starsEl = starsRef.current, railFill = railFillRef.current, nowDot = nowDotRef.current,
      clockEl = clockRef.current, capEl = capRef.current, orbEl = orbRef.current;
    if (!night || !skyD || !sunglow || !starsEl || !railFill || !nowDot || !clockEl || !capEl || !orbEl) return;

    const stamps = Array.from(night.querySelectorAll<HTMLElement>('.stamp'));
    const cards = Array.from(night.querySelectorAll<HTMLElement>('.rise'));
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    // the star field (fades out as dawn rises)
    if (!starsEl.childElementCount) {
      for (let i = 0; i < 90; i++) {
        const s = document.createElement('i');
        const z = Math.random();
        s.style.left = `${Math.random() * 100}%`;
        s.style.top = `${Math.random() * 100}%`;
        s.style.width = s.style.height = `${z * 2 + 0.6}px`;
        s.style.opacity = String(0.25 + z * 0.6);
        if (!reduce) s.style.animation = `mhv2tw ${2.4 + Math.random() * 2.6}s ease-in-out ${Math.random() * 3}s infinite`;
        starsEl.appendChild(s);
      }
    }

    // the clock is anchored to the STORY, not to even spacing: each stamp's
    // layout position maps to its timestamp; the clock lerps between them
    let H = 0;
    let anchors: { y: number; m: number }[] = [];
    const geometry = () => {
      const rr = night.getBoundingClientRect();
      H = night.offsetHeight;
      anchors = [{ y: 0, m: 0 }];
      stamps.forEach(s => {
        const r = s.getBoundingClientRect();
        anchors.push({ y: r.top + r.height / 2 - rr.top, m: Number(s.dataset.min) || 0 });
      });
      anchors.push({ y: H, m: 480 });
    };
    const clockAt = (y: number): number => {
      if (y <= anchors[0].y) return anchors[0].m;
      for (let i = 1; i < anchors.length; i++) {
        if (y <= anchors[i].y) {
          const a = anchors[i - 1], b = anchors[i];
          return lerp(a.m, b.m, (y - a.y) / Math.max(1, b.y - a.y));
        }
      }
      return 480;
    };

    const setNight = (revealY: number) => {
      const q = H ? revealY / H : 0;
      skyD.style.opacity = String(win(q, 0.45, 0.95));
      starsEl.style.opacity = String(1 - win(q, 0.35, 0.75));
      const sun = win(q, 0.6, 1);
      sunglow.style.opacity = String(sun);
      sunglow.style.transform = `translateX(-50%) translateY(${-(sun * 180)}px)`;
      railFill.style.height = `${revealY}px`;
      nowDot.style.top = `${revealY}px`;
      clockEl.textContent = fmtClock(clockAt(revealY));
      const w = win(q, 0.5, 1);
      orbEl.style.background = `rgb(${Math.round(lerp(203, 251, w))},${Math.round(lerp(213, 191, w))},${Math.round(lerp(225, 36, w))})`;
      orbEl.style.boxShadow = `0 0 ${lerp(6, 22, w)}px rgba(251,191,36,${lerp(0.15, 0.9, w)})`;
      capEl.textContent = q > 0.97 ? 'sunrise · the brief is ready' : 'the night shift · Avium is working';
      const rr = night.getBoundingClientRect();
      stamps.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        const lit = revealY >= r.top + r.height / 2 - rr.top - 2;
        s.classList.toggle('lit', lit);
        if (lit && cards[i - 1]) cards[i - 1].classList.add('in'); // the 11:02 strip has no card
      });
    };

    geometry();

    if (reduce) { // hold the 7am state — no motion
      setNight(H);
      return;
    }

    // the loop re-arms BEFORE the body runs and the body is guarded — no
    // single bad frame can kill the experience
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      try {
        const rr = night.getBoundingClientRect();
        setNight(Math.max(0, Math.min(H, window.innerHeight * 0.62 - rr.top)));
      } catch { /* skip the frame, keep the loop alive */ }
    };
    raf = requestAnimationFrame(tick);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(geometry) : null;
    ro?.observe(night);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); };
  }, []);

  return (
    <div className="mhv2 min-h-screen flex flex-col">
      <style>{CSS}</style>
      <div className="skyN" aria-hidden="true" />
      <div className="skyD" ref={skyDRef} aria-hidden="true" />
      <div className="sunglow" ref={sunglowRef} aria-hidden="true" />
      <div className="stars" ref={starsRef} aria-hidden="true" />

      <PublicHeader />

      {/* Hero — the claim, the punchline, ONE conversion CTA */}
      <div className="hero w">
        <h1>Hit delivery dates <span className="q">with confidence</span>.</h1>
        <p className="sub">You’ve been running them on gut feel.</p>
        <p className="punch">Avium delivers you answers every morning at <b>7 am</b>.</p>
        <div className="ctas">
          <Link className="btn-big" to="/login">Start free →</Link>
        </div>

        {/* the money shot: Knowledge Graph (evidence) + LEADERSHIP briefing (answer) */}
        <div className="shot2">
          <div className="kgp-panel">
            <div className="kgp-hd"><i>◆</i> Delivery Knowledge Graph <span>· your data, resolved</span></div>
            <KnowledgeGraphPreview variant="light" />
          </div>
          <div className="bfp-panel"><LeadershipBriefingPreview /></div>
        </div>
        <p className="shotcap">The Knowledge Graph is the <b>evidence</b>; the briefing is the <b>answer</b> — both grounded in the same Avium Signals, never invented.</p>

        <p className="handoff">You close the laptop on a gut feeling.<br /><b>You log off. Avium clocks in.</b></p>
      </div>

      {/* ══ THE NIGHT SHIFT — time is the connective tissue: 11 PM → 7 AM ══ */}
      <div className="night" ref={nightRef}>
        <div className="hud"><div className="hudpill">
          <span className="orb" ref={orbRef} aria-hidden="true" />
          <b ref={clockRef}>11:00 PM</b>
          <span className="cap" ref={capRef}>the night shift · Avium is working</span>
        </div></div>

        <div className="rail" aria-hidden="true" />
        <div className="railfill" ref={railFillRef} aria-hidden="true" />
        <div className="nowdot" ref={nowDotRef} aria-hidden="true" />

        <div className="nsec">
          <div className="stamp" data-min="2">11:02 PM</div>
          <div className="nstrip">
            One connection. <b>Jira</b>, <b>Azure DevOps</b>, <b>Asana</b>, or <b>Excel</b> — the night shift starts reading your delivery data while you sleep.
          </div>
        </div>

        <section className="nsec">
          <div className="stamp" data-min="156">1:36 AM</div>
          <div className="nbeat">
            <div className="txt">
              <div className="lab">The sweep</div>
              <h2>14 tripwires read every ticket.</h2>
              <p className="beatbody">Most platforms tell you what happened. While you sleep, deterministic Avium Signals sweep the whole board and rank what they find by urgency — by the time you wake, the Risk Ledger is written.</p>
            </div>
            <div className="gcard ledger rise">
              <div className="lhead"><span>RISK LEDGER</span><span>9 signals · ranked by urgency</span></div>
              <div className="lrow">
                <div className="top"><span className="rank">1</span><span className="ti">Sprint 12 is at risk</span><span className="pillOff">Off-target</span></div>
                <div className="mid"><span className="lmetric risk">61% on-time</span><span className="dt">85% of simulations land 3 days past the deadline. Drivers: 3 over-allocated · 10 blocked · 5 unassigned.</span></div>
                <div className="src">◆ <b>via Avium Signal · risk</b> · grounded, never invented</div>
              </div>
              <div className="lrow">
                <div className="top"><span className="rank">2</span><span className="ti">Product velocity dropped sharply</span><span className="pillOff">Off-target</span></div>
                <div className="mid"><span className="lmetric">−25%</span><span className="dt">Output fell to 63.1h last sprint — 25% below its recent average.</span>
                  <svg width="84" height="30" viewBox="0 0 84 30" aria-hidden="true"><polyline points="2,9 16,7 30,12 44,10 58,16 70,14 82,26" fill="none" stroke="#D97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <div className="src">◆ <b>via Avium Signal · velocity</b> · grounded, never invented</div>
              </div>
            </div>
          </div>
        </section>

        <section className="nsec">
          <div className="stamp" data-min="251">3:11 AM</div>
          <div className="nbeat">
            <div className="txt">
              <div className="lab">The odds</div>
              <h2>10,000 trials while you sleep.</h2>
              <p className="beatbody">Monte-Carlo simulation on your team’s real throughput — recomputed nightly, not estimated. The number waiting for you isn’t a feeling: it’s the odds, and the one lever that moves them before you commit.</p>
            </div>
            <div className="wif rise">
              <div className="delta">+23 pts<small>ON-TIME ODDS</small></div>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--mh-ink)' }}>Will Sprint 12 land on time?</div>
              <div className="wifrow">
                <div className="gbox today">
                  <OddsGauge pct={61} color="#EF4444" />
                  <div className="gl">TODAY</div>
                  <span className="chipL c-red">At risk</span>
                </div>
                <div className="lever">
                  <div className="arrowline">→</div>
                  <span className="lc">⚙ the move:<br />AV-216 · Nick → Sam</span>
                  <div className="arrowline" style={{ marginTop: 6 }}>→</div>
                </div>
                <div className="gbox">
                  <OddsGauge pct={84} color="#14B8A6" />
                  <div className="gl">YOUR SCENARIO</div>
                  <span className="chipL c-teal">On track</span>
                </div>
              </div>
              <div className="wiffoot">
                <span>10,000 paired trials · same seed — the delta is the lever, never noise</span>
                <Link className="mk" to="/login">Make it real →</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="nsec">
          <div className="stamp" data-min="387">5:27 AM</div>
          <div className="nbeat">
            <div className="txt">
              <div className="lab">The receipts</div>
              <h2>Every claim is wired to its ticket.</h2>
              <p className="beatbody">Before anything is allowed into your brief, it’s traced: signal → person → work → sprint. When they ask “how do you know?” — the receipts were attached at 5:27 AM. Grounded — never invented.</p>
            </div>
            <div className="gcard drill rise">
              <div className="dr sig"><span className="ic">🔗</span><div><div className="t">Blocked by dependency · 7 items</div><div className="m">Avium Signal · ranked #3 this morning</div></div><span className="badge b-amb">signal</span></div>
              <div className="hop">↓ <span>drill — which work?</span></div>
              <div className="dr"><span className="ic">🎟</span><div><div className="t">AV-215 · Payment webhook retries</div><div className="m">Sam Patel (62%) · In Review · stalled 6 days</div></div></div>
              <div className="hop">↓ <span>blocked by — the why</span></div>
              <div className="dr ghost"><span className="ic">👻</span><div><div className="t">AV-198 · Vendor API contract</div><div className="m">sits in the backlog — another team owns it</div></div><span className="badge b-red">not on this board</span></div>
              <div className="hop">↓ <span>impact — the date</span></div>
              <div className="dr impact"><span className="ic">⚠</span><div><div className="t">Sprint 12 · 61% on-time</div><div className="m">−6 days if AV-198 doesn’t move this week</div></div></div>
            </div>
          </div>
        </section>

        <section className="nsec kgwide">
          <div className="stamp" data-min="460">6:40 AM</div>
          <div className="nbeat">
            <div className="txt">
              <div className="lab">The graph resolves</div>
              <h2>Every ticket identifies its risk on the Knowledge Graph.</h2>
              <p className="beatbody">The tangle of the day resolves into structure — the who, the what, the work, the risk — the same graph from the top of the page, settled before sunrise.</p>
            </div>
            <div className="gcard kgcard rise">
              <div className="lhead"><span>KNOWLEDGE GRAPH</span><span>chaos → structure, on a loop</span></div>
              <div className="kgbody"><KnowledgeGraphPreview variant="light" /></div>
              <div className="kgfoot"><b>The who, the what, the when — and the risk</b>
                Signal → owner → ticket → the sprint. Click any node, open the ticket.</div>
            </div>
          </div>
        </section>

        <div className="quoteband">
          <div className="panel">
            <p>“More time acting as <span>a leader</span> instead of a reporter.”</p>
            <div className="who">— Ella Jones, PM</div>
          </div>
        </div>
      </div>

      {/* Sunrise — the night shift hands you the answer */}
      <div className="ctaband">
        <div className="when">7:00 AM</div>
        <h2>Your phone buzzes. The brief is waiting.</h2>
        <p>Everything the night shift found — the risk, the odds, the move — in your inbox before standup. Connect Jira, Azure DevOps or Asana free: sixty seconds, no credit card needed.</p>
        <div className="ctas"><Link className="btn-big" to="/login">Start free →</Link></div>
      </div>

      {/* Go deeper — the SEO cluster: every Signal + every persona stays linked from the hub */}
      <div className="deeper">
        <div className="w">
          <div className="cols">
            <div>
              <h3>Learn the signals</h3>
              <div className="links">
                {Object.entries(SIGNAL_LEARN_CONTENT).map(([slug, c]) => (
                  <Link key={slug} to={`/learn/${slug}`}>{c.signalName}</Link>
                ))}
              </div>
              <div className="hub"><Link to="/learn">All Signals →</Link></div>
            </div>
            <div>
              <h3>For your role</h3>
              <div className="links">
                {Object.entries(PERSONA_LEARN_CONTENT).map(([slug, c]) => (
                  <Link key={slug} to={`/for/${slug}`}>{c.role}</Link>
                ))}
              </div>
              <div className="hub"><Link to="/for">For your role →</Link></div>
            </div>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
